/**
 * SelectedEtfsSummary.jsx — Phase 36 (UI simplification).
 *
 * Unified summary of every ETF the user has selected — whether picked
 * from the database table or pasted as ISIN. Provides:
 *
 *   • Per-ETF row with name + ticker + ISIN + source badge + remove btn
 *   • Editable weight input column (% allocation)
 *   • Total summary footer + warning when |total - 100| ≥ 0.01
 *   • "Ribilancia equamente" button (resets manualAllocation flag)
 *   • Placeholder rows for ISINs currently being imported by an async
 *     bulk-isin-paste job (Q4 option C — inline progress)
 *   • Toast-like persistent banners for `partial` (no holdings) and
 *     `failed` (ISIN not found) paste outcomes
 *
 * Pure presentation: receives the weight map + handlers from a hook
 * (`useEtfWeights`) owned by the parent.
 */
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { EmptyState } from "@/components/system";
import { formatPercentPoints } from "@/utils/format";
import { X, AlertTriangle, Loader2, RotateCcw, Info, Inbox } from "lucide-react";


// --------------------------------------------------------------------
// Helpers
// --------------------------------------------------------------------

/**
 * Format a percentage with up to 4 decimals, but trim trailing zeros so
 * "33.3333" stays compact and "25" doesn't render as "25.0000".
 */
function fmtPct(v) {
  if (v === null || v === undefined || Number.isNaN(v)) return "";
  const n = Number(v);
  if (Number.isInteger(n)) return String(n);
  return n.toFixed(4).replace(/\.?0+$/, "");
}

function sourceBadge(source) {
  if (source === "paste") {
    return (
      <Badge
        variant="secondary"
        className="text-label bg-brand-subtle/40 text-brand-accent border-brand-border"
        data-testid="source-badge-paste"
      >
        ISIN
      </Badge>
    );
  }
  return (
    <Badge
      variant="secondary"
      className="text-label bg-surface-subtle text-content-secondary border-line"
      data-testid="source-badge-db"
    >
      Catalogo
    </Badge>
  );
}


// --------------------------------------------------------------------
// Sub-components
// --------------------------------------------------------------------

function EtfRow({ etf, weight, onWeightChange, onRemove, disabled }) {
  return (
    <div
      className="flex flex-wrap items-center gap-x-2 gap-y-2 border-b border-line px-3 py-2 last:border-b-0 hover:bg-surface/50"
      data-testid={`selected-etf-row-${etf.isin}`}
    >
      {/* Phase 6 — at 390px identity, weight and remove get real room: the row
          wraps instead of squeezing a 3-column table off the viewport. */}
      <div className="min-w-0 basis-full sm:basis-0 sm:flex-1 text-sm text-content">
        <div className="flex items-center gap-2 flex-wrap">
          {sourceBadge(etf.source)}
          <span className="font-medium break-words" data-testid={`etf-name-${etf.isin}`}>
            {etf.etf_name || "(senza nome)"}
          </span>
        </div>
        <div className="mt-0.5 flex flex-wrap items-center gap-x-2 text-label text-content-muted">
          {etf.ticker && (
            <span data-testid={`etf-ticker-${etf.isin}`}>{etf.ticker}</span>
          )}
          {etf.ticker && etf.isin && <span aria-hidden="true">·</span>}
          <span data-testid={`etf-isin-${etf.isin}`}>{etf.isin}</span>
          {etf.asset_type && (
            <>
              <span aria-hidden="true">·</span>
              <span>{etf.asset_type}</span>
            </>
          )}
        </div>
      </div>
      <div className="flex items-center gap-1 ml-auto">
        <input
          type="number"
          min="0"
          max="100"
          step="0.01"
          value={fmtPct(weight)}
          onChange={(e) => onWeightChange(etf.isin, e.target.value)}
          disabled={disabled}
          className="w-20 h-10 rounded border border-line bg-surface-subtle px-2 text-right text-sm text-content focus:outline-none focus-visible:border-focus focus-visible:ring-2 focus-visible:ring-focus/40 disabled:opacity-50"
          data-testid={`weight-input-${etf.isin}`}
          aria-label={`Peso percentuale per ${etf.etf_name}`}
        />
        <span className="text-xs text-content-muted">%</span>
        <button
          type="button"
          onClick={() => onRemove(etf.isin)}
          disabled={disabled}
          className="flex items-center justify-center w-10 h-10 rounded text-content-muted hover:bg-surface-subtle hover:text-danger disabled:opacity-30 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus"
          aria-label={`Rimuovi ${etf.etf_name}`}
          data-testid={`remove-btn-${etf.isin}`}
        >
          <X className="h-4 w-4" aria-hidden="true" />
        </button>
      </div>
    </div>
  );
}


function PendingPasteRow({ isin }) {
  return (
    <div
      className="border-b border-line px-3 py-2 last:border-b-0 bg-brand-subtle/20 text-sm text-content-secondary"
      data-testid={`pending-paste-row-${isin}`}
    >
      <div className="flex flex-wrap items-center gap-2">
        <Loader2 className="h-3.5 w-3.5 animate-spin text-brand-accent" aria-hidden="true" />
        <span className="font-mono text-xs">{isin}</span>
        <span className="text-xs text-content-muted">— importazione in corso…</span>
      </div>
    </div>
  );
}


function PasteOutcomeBanner({ kind, etfs, onDismiss }) {
  // kind ∈ { partial, failed }
  if (!etfs || etfs.length === 0) return null;
  const isFailed = kind === "failed";
  return (
    <div
      className={`mb-2 flex items-start gap-2 rounded border px-3 py-2 text-xs ${
        isFailed
          ? "border-danger-border/50 bg-danger-surface/30 text-danger"
          : "border-warning-border/50 bg-warning-surface/30 text-warning"
      }`}
      role="status"
      data-testid={`paste-banner-${kind}`}
    >
      <Info className="h-3.5 w-3.5 mt-0.5 flex-shrink-0" />
      <div className="flex-1">
        <div className="font-medium mb-0.5">
          {isFailed
            ? `${etfs.length} ISIN non importati`
            : `${etfs.length} ETF importati ma non inclusi nel portafoglio`}
        </div>
        <div className="text-label opacity-90">
          {isFailed
            ? "Verifica che gli ISIN siano corretti e che si tratti di ETF UCITS."
            : "Emittente non supportato al momento per il download automatico degli holdings."}
        </div>
        <ul className="mt-1 space-y-0.5 font-mono text-label opacity-80">
          {etfs.slice(0, 5).map((e) => (
            <li key={e.isin}>
              {e.isin}
              {e.etf_name ? ` — ${e.etf_name}` : ""}
              {e.reason ? ` (${e.reason})` : ""}
            </li>
          ))}
          {etfs.length > 5 && <li>… e altri {etfs.length - 5}</li>}
        </ul>
      </div>
      {onDismiss && (
        <button
          type="button"
          onClick={onDismiss}
          className="flex items-center justify-center w-10 h-10 -m-1 rounded opacity-60 hover:opacity-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus"
          aria-label="Chiudi l'avviso"
        >
          <X className="h-3.5 w-3.5" />
        </button>
      )}
    </div>
  );
}


// --------------------------------------------------------------------
// Main component
// --------------------------------------------------------------------

/**
 * Props:
 *   - selectedEtfs[]:      { isin, ticker, etf_name, asset_type, source }
 *   - weights:             { [isin]: pct }
 *   - manualAllocation:    bool
 *   - totalWeight:         number
 *   - isValid:             bool
 *   - pendingPasteIsins[]: string[]  ISINs currently being imported
 *   - pastePartial[]:      { isin, etf_name, reason }  toast banner
 *   - pasteFailed[]:       { isin, reason }            toast banner
 *   - onWeightChange(isin, value)
 *   - onRemove(isin)
 *   - onResetEqual()
 *   - onDismissPartial()
 *   - onDismissFailed()
 *   - disabled: bool       (e.g. analysis in flight)
 */
export function SelectedEtfsSummary({
  selectedEtfs = [],
  weights = {},
  manualAllocation = false,
  totalWeight = 0,
  isValid = false,
  pendingPasteIsins = [],
  pastePartial = [],
  pasteFailed = [],
  onWeightChange,
  onRemove,
  onResetEqual,
  onDismissPartial,
  onDismissFailed,
  disabled = false,
}) {
  const hasContent =
    selectedEtfs.length > 0 ||
    pendingPasteIsins.length > 0 ||
    pastePartial.length > 0 ||
    pasteFailed.length > 0;

  if (!hasContent) {
    return (
      <div className="rounded border border-line bg-surface-subtle/40">
        <EmptyState
          testId="selected-etfs-empty"
          icon={Inbox}
          title="Nessun ETF selezionato"
          description="Incolla degli ISIN oppure scegli gli ETF dal catalogo qui sopra: compariranno qui con i loro pesi."
        />
      </div>
    );
  }

  const totalDelta = totalWeight - 100;
  const showWarning = !isValid && selectedEtfs.length > 0;

  return (
    <div data-testid="selected-etfs-summary">
      <PasteOutcomeBanner
        kind="failed"
        etfs={pasteFailed}
        onDismiss={onDismissFailed}
      />
      <PasteOutcomeBanner
        kind="partial"
        etfs={pastePartial}
        onDismiss={onDismissPartial}
      />

      <div className="overflow-hidden rounded border border-line bg-surface-subtle/40">
        <div className="flex items-center justify-between border-b border-line px-3 py-2 bg-surface/50">
          <h3 className="text-sm font-semibold text-content">
            ETF selezionati
            <span className="ml-2 text-xs font-normal text-content-muted">
              ({selectedEtfs.length}
              {pendingPasteIsins.length > 0 &&
                ` + ${pendingPasteIsins.length} in import`}
              )
            </span>
          </h3>
          {manualAllocation && selectedEtfs.length > 0 && (
            <Button
              size="sm"
              variant="ghost"
              onClick={onResetEqual}
              disabled={disabled}
              title={disabled ? "Non disponibile durante un'analisi in corso" : undefined}
              className="h-10 text-label text-content-secondary hover:text-content"
              data-testid="reset-equal-btn"
            >
              <RotateCcw className="mr-1 h-3.5 w-3.5" />
              Ribilancia equamente
            </Button>
          )}
        </div>

        <div>
          {selectedEtfs.map((etf) => (
            <EtfRow
              key={etf.isin}
              etf={etf}
              weight={weights[etf.isin] ?? 0}
              onWeightChange={onWeightChange}
              onRemove={onRemove}
              disabled={disabled}
            />
          ))}
          {pendingPasteIsins.map((isin) => (
            <PendingPasteRow key={`pending-${isin}`} isin={isin} />
          ))}
        </div>

        <div className="flex flex-wrap items-center justify-between gap-2 border-t border-line bg-surface/30 px-3 py-2">
          <span className="text-xs font-medium text-content-secondary">
            Totale allocazione
          </span>
          <span>
            <span
              className={`text-sm font-semibold tabular ${
                isValid ? "text-content" : "text-warning"
              }`}
              data-testid="total-weight"
            >
              {formatPercentPoints(totalWeight)}
            </span>
            <span className="ml-1 text-label text-content-muted">di 100 %</span>
          </span>
        </div>
      </div>

      {showWarning && (
        <div
          className="mt-2 flex items-center gap-2 rounded border border-warning-border/50 bg-warning-surface/30 px-3 py-2 text-xs text-warning"
          role="alert"
          data-testid="weight-warning"
        >
          <AlertTriangle className="h-3.5 w-3.5 flex-shrink-0" />
          <span>
            La somma dei pesi è{" "}
            <strong>{formatPercentPoints(totalWeight)}</strong> ({totalDelta > 0 ? "+" : ""}
            {formatPercentPoints(totalDelta)}). Puoi proseguire: prima di avviare
            l'analisi ti verrà chiesta una conferma.
          </span>
        </div>
      )}
    </div>
  );
}

export default SelectedEtfsSummary;
